import {Injectable} from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { bind } from 'lodash-decorators/bind';

import { environment as env } from '@patientlynk3/environment';
import { ApiService } from '@patientlynk3/services/api/api.service';
import { LabsService } from '@patientlynk3/services/labs/labs.service';
import { ProfileService } from '@patientlynk3/services/profile/profile.service';
import { NotificationStore } from '@patientlynk3/services/notifications/notification.store';
import { AuthService } from '@patientlynk3/services/auth/auth.service'
import { Router } from '@angular/router';

import {
  ActivityGroup,
  Feed,
  StreamResponse,
  StreamSocketResponse,
  StreamRetrieveOptions
} from '@patientlynk3/models';

import {
  NotificationQuery,
  NOTIFICATION_PAGE_LIMIT
} from '@patientlynk3/services/notifications/notification.query';
import { LabVisit } from '../labs/lab.model';
import { Logger } from '../logger/logger.service';

let stream: any = import('getstream').catch(null);

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private client: stream.Client;
  private _tokens: { token: string[], id: string[] };
  private feeds: { id: string, feed: Feed }[];
  private currentPage = 1;

  public list$: Observable<ActivityGroup[]>;
  public unseen = 0;
  public unread = 0;
  public next = false;

  public status: 'loading' | 'error' | 'complete' = 'loading';
  public retried = false;


  constructor(private API: ApiService, private labs: LabsService, private profile: ProfileService, private router: Router,
              private store: NotificationStore, private query: NotificationQuery, private auth: AuthService) {
                this.auth.isLoggedIn$.subscribe((status) => {
                  status ? this.load() : this.clear();
                });
              }

  async load(): Promise<void> {
    this.status = 'loading';
    stream = await stream;

    this.client = stream.connect(env.stream.APP_TOKEN, null, env.stream.APP_ID);
    this._tokens ||= await this.API.getStreamToken()
      .pipe(catchError(() => null)).toPromise().catch(null);

    if (!this._tokens) {
      this.status = 'error';
      this.clear();
      return;
    }

    this.store.update({ user: this._tokens.id[0] });
    this.currentPage = 1;
    this.list$ = this.query.list$;

    this.feeds = this._tokens.token?.map(tokenMap => {
      const [id, token] = Object.entries(tokenMap)[0];
      /* @ts-ignore: next-line */
      const feed = this.client.feed('member_notification', id, token);
      feed.subscribe(response => this.onReceiveNotification(response));

      return { id, feed };
    });

    this.retrieve();
  }

  private async retrieve(
    { seen = false, read = false }: StreamRetrieveOptions = {}
  ): Promise<void> {
    const config = {
      offset: NOTIFICATION_PAGE_LIMIT * (this.currentPage - 1),
      limit: NOTIFICATION_PAGE_LIMIT,
      mark_seen: seen,
      mark_read: read
    };

    // A lot is going on in these few lines... I'll document it soon.
    const streams = await Promise.all(this.feeds.map(async userFeed => {
      const { id: feedId, feed } = userFeed;
      const { id: myId, dependents } = this.profile.getValue();

      const userStream = await feed.get(config) as StreamResponse;
      const person = dependents?.find(dep => dep.abartys_id === feedId)?.id || myId;

      userStream.results.forEach(result => result.person = person);
      userStream.results.forEach(result => result.userId = feed.userId);
      return userStream;
    }));

    const unseen = streams.reduce((total, current) => total + current.unseen, 0);
    const unread = streams.reduce((total, current) => total + current.unread, 0);
    const next = streams.reduce((total, current) => !!total || !!current.next, false);
    const results = streams.flatMap(stream => stream.results);

    this.unseen = unseen;
    this.unread = unread;
    this.next = next;

    results.forEach(this.enrichNotificationGroup);
    this.status = 'complete';
  }

  clear(): void {
    this._tokens = null;
    this.list$ = null;
    this.feeds?.forEach(({ feed }) => feed.unsubscribe());
    this.feeds = null;
  }

  markAllAs({ ...params }: StreamRetrieveOptions): void {
    let items;

    if (params.seen) {
      items = this.query.getAll({
        filterBy: ({ is_seen }) => !is_seen
      });
      this.store.update(
        [ ...items.map(item => item.id) ],
        { is_seen: true }
      );
    } else if (params.read) {
      items = this.query.getAll({
        filterBy: ({ is_read }) => !is_read
      });
      this.store.update(
        [ ...items.map(item => item.id) ],
        { is_read: true }
      );
    }

    if (items.length) {
      this.retrieve({ ...params });
    }

  }

  async onGetNextNotifications(): Promise<void> {
    if (this.next) {
      this.next = false;
      this.currentPage += 1;
      await this.retrieve({ read: true });
      this.query.loadToPage(this.currentPage);
    }
  }

  async onReceiveNotification(response: StreamSocketResponse): Promise<void> {
    if (!response.new.length) {
      return;
    }
    console.warn('notification object received:', response.new[0])
    
    if (response.new[0].translate === 'newLabReceived') { 
      
      let urlLabID:string = response.new[0].object.replaceAll('|','%7C')
      urlLabID = urlLabID.replaceAll('-',':')

      console.warn('router.url',this.router.url)
      console.warn('urlLabID',urlLabID)
      
      console.warn('lab notification is in current view?', this.router.url.includes(urlLabID))

      if (this.router.url.includes(urlLabID)) {                      
        this.router.navigate([this.router.url])
        console.warn('lab view reloaded!')
      }
      
      /*
      //  implementing the cache storage system

      const myLabID = response.new[0].object.replace('-',':')
      let orderDate = new Date(response.new[0].order_date);

      // sets verbose date from response to YYYY-MM-DD
      let dateFormatted:string = orderDate.getFullYear() + "-" + 
                      ((orderDate.getMonth() + 1).toString().length != 2 ? "0" + 
                      (orderDate.getMonth() + 1) : (orderDate.getMonth() + 1)) + "-" + 
                      (orderDate.getDate().toString().length != 2 ? "0" + 
                      orderDate.getDate() : orderDate.getDate());

      const labResults = await this.API.getLabResult(myLabID, dateFormatted).toPromise().catch(error => {
        Logger.error(`Problem accessing lab-result:${myLabID}`, error);
      }) as LabVisit;

      console.warn('labResults reloaded:', labResults)
      this.labs.saveLabData(myLabID + '&' + dateFormatted, labResults)
      */
      this.labs.refresh("", "", null);
      this.profile.refresh();
    }

    this.retrieve();
  }

  async deleteActivity(notification: ActivityGroup) {
    const { feed } = this.feeds.find(({ feed }) => feed.userId === notification.userId);

    if (!feed) {
      return;
    }

    this.store.remove(notification.id);
    await feed.removeActivity(notification.activities[0].id).catch(null);
  }

  refreshFeed(){
    this.retrieve();
  }

  // Store enrichment responses locally to prevent repeat API and
  // GetStream requests. Number of activities and `id` must match.
  @bind()
  private async enrichNotificationGroup(group: ActivityGroup): Promise<void> {
    const existingGroup = this.query.getEntity(group.id);
    let newGroup;

    if (existingGroup?.activities.length !== group.activities.length) {
      newGroup = await this.API
        .enrichment({ activities: group.activities })
        .pipe(map(response => response.detail))
        .toPromise();
    } else {
      newGroup = {
        actor: existingGroup?.actor || group.actor,
        activities: existingGroup?.activities || group.activities
      };
    }

    const { lab_name, translate, object, order_date } = group.activities[0];
    group.actor = lab_name || newGroup.actor;

    if (order_date) {
      let n = 0 , N = 2; 
      group.link = '/labs/' + object.replace(/-/g, match => n++ < N ? ":" : match)
        + '&' + (new Date(order_date)).toISOString().split('T')[0];        
    }

    group.translate = translate;
    group.activities = group.activities
      .map((activity, index) =>
        Object.assign(activity, newGroup.activities[index]));
    group.source = group.activities
      .map(activity => activity.source)
      .filter(source => source);

    this.store.upsertMany([group]);
  }
}
